import { InputAdornment } from "@mui/material";

import { FinalDecisionStatus } from "./types";

// Limit chosen to align with the database DECIMAL(15,2) constraint for currency fields
// (e.g., incumbent_annual_premium, winning_annual_premium), preventing values that
// would overflow the 13-digit integer portion before the 2 decimal places.
const MAX_DECIMAL_BEFORE_EXPONENTIAL = 1e13;

export const limitToTwoDecimals = (value: string): string | null => {
  const normalized = value.replace(",", ".");

  if (/\.\d{3,}/.test(normalized)) {
    return null;
  }

  if ((normalized.match(/\./g) ?? []).length > 1) {
    return null;
  }

  if (normalized.includes(".")) {
    return normalized;
  }

  const number = parseFloat(normalized);

  if (Number.isNaN(number)) {
    return "";
  }

  if (Math.abs(number) >= MAX_DECIMAL_BEFORE_EXPONENTIAL) {
    return null;
  }

  return (Math.floor(number * 100) / 100).toString();
};

export const FORM_FIELDS = {
  INCUMBENT_DATA: "incumbentData",
  INCUMBENT_PRODUCT_ID: "incumbentProductId",
  INCUMBENT_CARRIER_ID: "incumbentCarrierId",
  INCUMBENT_ANNUAL_PREMIUM: "incumbentAnnualPremium",
  INCUMBENT_GA_OVERRIDE_PERCENTAGE: "incumbentGaOverridePercentage",
  INCUMBENT_GA_ANNUAL_OVERRIDE_AMOUNT: "incumbentGaAnnualOverrideAmount",
  WINNING_CONTRACTED_CARRIER: "winningContractedCarrier",
  INCUMBENT_CONTRACTED_CARRIER: "incumbentContractedCarrier",
  WINNING_CARRIER_ID: "winningCarrierId",
  FINAL_DECISION_STATUS: "finalDecisionSelected",
  ANNUAL_WRITTEN_PREMIUM: "annualWrittenPremium",
  WINNING_GA_OVERRIDE_PERCENTAGE: "winningGaOverridePercentage",
  WINNING_GA_ANNUAL_OVERRIDE_AMOUNT: "winningGaAnnualOverrideAmount",
  LAST_PAYMENT: "lastPayment",
  RENEWAL_DATE: "renewalDate",
  RENEWAL_DATE_MANUAL_ENTRY: "renewalDateManualEntry", // internal use only
  SOLD: "sold",
  RATE_GUARANTEE: "rateGuarantee",
  TECH_CREDITS: "techCredits",
  NOTES: "notes",
  NUMBER_OF_PLANS_QUOTED: "numberOfPlansQuoted",
  LAST_EFFECTIVE_DATE: "lastEffectiveDate", // internal use only
  LAST_RATE_GUARANTEE: "lastRateGuarantee", // internal use only
  INCUMBENT_PRODUCT_ID_MANUAL_ENTRY: "incumbentProductIdManualEntry", // internal use only
} as const;

export const YES = "y" as const;
export const NO = "n" as const;

export const FINAL_DECISION_STATUS_LABELS: Record<FinalDecisionStatus, string> =
  {
    [FinalDecisionStatus.MovingCarriers]: "Moving Carriers",
    [FinalDecisionStatus.RenewingWithIncumbent]: "Renewing with Incumbent",
    [FinalDecisionStatus.TerminatingCoverage]: "Terminating Coverage",
    [FinalDecisionStatus.SoldNewLine]: "Sold New Line",
    [FinalDecisionStatus.BORNotWon]: "BOR Not Won",
    [FinalDecisionStatus.LostBOR]: "Lost BOR",
    [FinalDecisionStatus.DidNotSell]: "Did Not Sell",
  } as const;

export const SELECT_OPTION_COVERAGE_FIELD = {
  name: "selectOptionCoverage",
  label:
    "Please select an option for each line of coverage to submit the RFP final decisions",
  type: "label",
  variant: "body-large",
} as const;

export const WINNING_CARRIER_FIELD = {
  type: "autocomplete",
  name: FORM_FIELDS.WINNING_CARRIER_ID,
  label: "Winning Carrier",
  grid: { size: 7 },
} as const;

export const RENEWAL_DATE_FIELD = {
  type: "date",
  name: FORM_FIELDS.RENEWAL_DATE,
  label: "Next Renewal Date",
  grid: { size: 7 },
  required: true,
} as const;

const BASE_NUMERIC_FIELD = {
  type: "text",
  transform: (value: string) => limitToTwoDecimals(value),
} as const;

export const MONEY_FIELD = {
  ...BASE_NUMERIC_FIELD,
  customProps: {
    slotProps: {
      input: {
        startAdornment: <InputAdornment position="start">$</InputAdornment>,
      },
    },
  },
} as const;

export const ANNUAL_WRITTEN_PREMIUM_FIELD = {
  ...MONEY_FIELD,
  name: FORM_FIELDS.ANNUAL_WRITTEN_PREMIUM,
  label: "Annual Written Premium",
  grid: { size: 7 },
} as const;

export const PERCENTAGE_FIELD = {
  ...BASE_NUMERIC_FIELD,
  customProps: {
    slotProps: {
      input: {
        endAdornment: <InputAdornment position="end">%</InputAdornment>,
      },
    },
  },
} as const;

export const RATE_GUARANTEE_FIELD = {
  ...BASE_NUMERIC_FIELD,
  name: FORM_FIELDS.RATE_GUARANTEE,
  label: "Rate Guarantee (Years)",
  grid: { size: 7 },
} as const;

export const NUMBER_OF_PLANS_QUOTED_FIELD = {
  ...BASE_NUMERIC_FIELD,
  name: FORM_FIELDS.NUMBER_OF_PLANS_QUOTED,
  label: "Number of Plans Quoted",
  grid: { size: 7 },
  customProps: { required: true },
} as const;

export const TECH_CREDIT_FIELD = {
  name: FORM_FIELDS.TECH_CREDITS,
  label: "Tech Credits",
  type: "checkbox",
  grid: { size: 7 },
} as const;

export const NOTES_FIELD = {
  name: FORM_FIELDS.NOTES,
  label: "Notes",
  type: "textarea",
  grid: { size: 7 },
  customProps: {
    rows: 1,
    maxLength: 500,
    characterCounterVisible: true,
    fullWidth: true,
    placeholder: "Notes",
    characterLimitMode: false,
  },
} as const;

export const WINNING_CONTRACTED_CARRIER_FIELD = {
  type: "contractedCarrier",
  name: FORM_FIELDS.WINNING_CONTRACTED_CARRIER,
  label: "",
  grid: { size: 7 },
} as const;

export const WINNING_GA_OVERRIDE_PERCENTAGE_FIELD = {
  ...PERCENTAGE_FIELD,
  name: FORM_FIELDS.WINNING_GA_OVERRIDE_PERCENTAGE,
  label: "Winning GA Override Percentage",
  grid: { size: 7 },
} as const;

export const WINNING_GA_ANNUAL_OVERRIDE_AMOUNT_FIELD = {
  ...MONEY_FIELD,
  name: FORM_FIELDS.WINNING_GA_ANNUAL_OVERRIDE_AMOUNT,
  label: "Winning GA Override Annual Amount",
  grid: { size: 7 },
} as const;
